import React, { useState } from 'react';

const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;
const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() });

const request = async (url, method, body) => {
  const res = await fetch(url, { method, headers: jsonHeaders(), body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data.errors || [data.error]).filter(Boolean).join('. ') || 'Request failed');
  return data;
};

const DOMAIN_STATUS = {
  verified: ['Verified', 'bg-green-50 text-green-700'],
  pending: ['Pending', 'bg-amber-50 text-amber-700'],
  error: ['Error', 'bg-red-50 text-red-700']
};

const StatusBadge = ({ status }) => {
  const [label, cls] = DOMAIN_STATUS[status] || [status, 'bg-slate-100 text-slate-600'];
  return <span className={`text-xs font-medium px-2 py-0.5 rounded ${cls}`}>{label}</span>;
};

const Check = ({ ok, label }) => (
  <span className={`text-xs ${ok ? 'text-green-700' : 'text-slate-400'}`}>{ok ? '✓' : '✕'} {label}</span>
);

const DomainCard = ({ domain, onChange, onRemove }) => {
  const [open, setOpen] = useState(domain.status !== 'verified');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const check = async () => {
    setBusy(true);
    setError('');
    try {
      onChange(await request(`/email_domains/${domain.id}/check`, 'POST'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='border border-slate-200 rounded-lg'>
      <div className='flex flex-wrap items-center gap-3 px-4 py-3'>
        <span className='font-medium text-slate-900'>{domain.name}</span>
        <StatusBadge status={domain.status} />
        <div className='flex gap-3'>
          <Check ok={domain.spf_valid} label='SPF' />
          <Check ok={domain.dkim_valid} label='DKIM' />
          <Check ok={domain.dmarc_valid} label='DMARC' />
        </div>
        <span className='text-xs text-slate-400'>
          {domain.last_checked_at ? `checked ${new Date(domain.last_checked_at).toLocaleString()}` : 'not checked yet'}
        </span>
        <div className='ml-auto flex items-center gap-3'>
          <button onClick={() => setOpen(!open)} className='text-sm text-slate-600 hover:text-slate-900'>
            {open ? 'Hide DNS' : 'DNS records'}
          </button>
          <button onClick={check} disabled={busy} className='text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50'>
            {busy ? 'Checking…' : 'Check now'}
          </button>
          <button onClick={() => onRemove(domain)} className='text-sm text-red-600 hover:text-red-800'>Delete</button>
        </div>
      </div>
      {(domain.last_error || error) && <div className='px-4 pb-2 text-xs text-red-600'>{error || domain.last_error}</div>}
      {open && (
        <div className='border-t border-slate-100 px-4 py-3 bg-slate-50/60'>
          <p className='text-xs text-slate-500 mb-2'>
            Add these records at your DNS provider, then press “Check now”. Emails can be sent only after SPF, DKIM and DMARC are valid.
          </p>
          <table className='w-full text-xs'>
            <thead>
              <tr className='text-left text-slate-500'>
                <th className='py-1 pr-3 font-medium'>Type</th>
                <th className='py-1 pr-3 font-medium'>Host</th>
                <th className='py-1 pr-3 font-medium'>Value</th>
                <th className='py-1 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {domain.dns_records.map((r, i) => (
                <tr key={i} className='border-t border-slate-200/70 align-top'>
                  <td className='py-1.5 pr-3 font-mono'>{r.type}</td>
                  <td className='py-1.5 pr-3 font-mono break-all'>{r.name}</td>
                  <td className='py-1.5 pr-3 font-mono break-all'>{r.value}</td>
                  <td className='py-1.5'>{r.valid ? <span className='text-green-700'>valid</span> : <span className='text-amber-700'>missing</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const emptyForm = (domains) => ({ email_domain_id: domains[0]?.id || '', from_local: '', from_name: '', reply_to: '', rate_limit: '' });

const SenderForm = ({ initial, domains, defaultRateLimit, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...initial, rate_limit: initial.rate_limit || '' } : emptyForm(domains));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const domain = domains.find((d) => String(d.id) === String(form.email_domain_id));
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { email_sender: { ...form, rate_limit: form.rate_limit || null } };
      const data = initial ? await request(`/email_senders/${initial.id}`, 'PATCH', body) : await request('/email_senders', 'POST', body);
      onSaved(data, !!initial);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const input = 'mt-1 block w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-slate-900';
  return (
    <div className='space-y-3'>
      <label className='block text-xs font-medium text-slate-500'>
        Domain *
        <select className={input} value={form.email_domain_id} onChange={set('email_domain_id')}>
          {domains.map((d) => (
            <option key={d.id} value={d.id}>{d.name}{d.status !== 'verified' ? ` (${d.status})` : ''}</option>
          ))}
        </select>
      </label>
      <label className='block text-xs font-medium text-slate-500'>
        From Address *
        <div className='mt-1 flex items-center'>
          <input className='block w-full border border-slate-300 rounded-l px-2 py-1.5 text-sm text-slate-900' value={form.from_local} onChange={set('from_local')} placeholder='promo' />
          <span className='px-2 py-1.5 text-sm text-slate-500 bg-slate-100 border border-l-0 border-slate-300 rounded-r'>@{domain?.name || 'domain'}</span>
        </div>
      </label>
      <label className='block text-xs font-medium text-slate-500'>
        From Name * <span className='font-normal text-slate-400'>— what recipients see</span>
        <input className={input} value={form.from_name} onChange={set('from_name')} placeholder='Paripesa' />
      </label>
      <label className='block text-xs font-medium text-slate-500'>
        Reply-To <span className='font-normal text-slate-400'>— optional</span>
        <input className={input} value={form.reply_to || ''} onChange={set('reply_to')} placeholder='support@example.com' />
      </label>
      <label className='block text-xs font-medium text-slate-500'>
        Rate limit, emails per minute <span className='font-normal text-slate-400'>— empty = {defaultRateLimit}</span>
        <input type='number' min='1' className={input} value={form.rate_limit} onChange={set('rate_limit')} placeholder={String(defaultRateLimit)} />
      </label>
      {error && <p className='text-sm text-red-600'>{error}</p>}
      <div className='flex justify-end gap-2 pt-2'>
        <button onClick={onCancel} className='px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded'>Cancel</button>
        <button onClick={submit} disabled={saving} className='px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50'>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Add sender'}
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
    <div className='bg-white rounded-lg shadow-xl w-full max-w-lg' onClick={(e) => e.stopPropagation()}>
      <div className='flex items-center justify-between px-5 py-3 border-b border-slate-200'>
        <h3 className='text-base font-semibold text-slate-900'>{title}</h3>
        <button onClick={onClose} className='text-slate-400 hover:text-slate-600'>✕</button>
      </div>
      <div className='p-5'>{children}</div>
    </div>
  </div>
);

const EmailSendersManager = ({ initialSenders, initialDomains, defaultRateLimit, providerConfigured }) => {
  const [senders, setSenders] = useState(initialSenders || []);
  const [domains, setDomains] = useState(initialDomains || []);
  const [newDomain, setNewDomain] = useState('');
  const [modal, setModal] = useState(null);
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);

  const updateDomain = (domain) => {
    setDomains((prev) => prev.map((d) => (d.id === domain.id ? domain : d)));
    setSenders((prev) => prev.map((s) => (s.email_domain_id === domain.id
      ? { ...s, domain_status: domain.status, sendable: s.active && domain.status === 'verified' } : s)));
  };

  const addDomain = async () => {
    setAdding(true);
    setNotice(null);
    try {
      const domain = await request('/email_domains', 'POST', { email_domain: { name: newDomain } });
      setDomains((prev) => [...prev, domain].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDomain('');
    } catch (e) {
      setNotice({ error: e.message });
    } finally {
      setAdding(false);
    }
  };

  const removeDomain = async (domain) => {
    if (!window.confirm(`Delete domain ${domain.name}?`)) return;
    try {
      await request(`/email_domains/${domain.id}`, 'DELETE');
      setDomains((prev) => prev.filter((d) => d.id !== domain.id));
    } catch (e) {
      setNotice({ error: e.message });
    }
  };

  const toggle = async (sender) => {
    try {
      const data = await request(`/email_senders/${sender.id}/toggle_active`, 'PATCH');
      setSenders((prev) => prev.map((s) => (s.id === data.id ? data : s)));
    } catch (e) {
      setNotice({ error: e.message });
    }
  };

  const removeSender = async (sender) => {
    if (!window.confirm(`Delete sender ${sender.from_address}?`)) return;
    try {
      await request(`/email_senders/${sender.id}`, 'DELETE');
      setSenders((prev) => prev.filter((s) => s.id !== sender.id));
    } catch (e) {
      setNotice({ error: e.message });
    }
  };

  const onSaved = (sender, editing) => {
    setSenders((prev) => (editing ? prev.map((s) => (s.id === sender.id ? sender : s)) : [...prev, sender]));
    setModal(null);
  };

  return (
    <div className='min-h-screen bg-slate-50/50'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <div className='mb-6'>
          <h1 className='text-2xl font-semibold text-slate-900'>Email Senders</h1>
          <p className='mt-1 text-sm text-slate-600'>Sending domains and From addresses for email campaigns.</p>
        </div>

        {!providerConfigured && (
          <div className='mb-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3'>
            Mailgun is not connected yet (no <code>MAILGUN_API_KEY</code>). Domain checks and sending use the local mock until the key is set.
          </div>
        )}
        {notice?.error && <div className='mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2'>{notice.error}</div>}

        <section className='bg-white rounded-xl border border-slate-200/70 shadow-sm p-5 mb-6'>
          <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
            <h2 className='text-lg font-semibold text-slate-900'>Domains</h2>
            <div className='flex items-center gap-2'>
              <input className='border border-slate-300 rounded px-3 py-1.5 text-sm w-64' placeholder='mail.example.com' value={newDomain} onChange={(e) => setNewDomain(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && newDomain && addDomain()} />
              <button onClick={addDomain} disabled={!newDomain || adding} className='px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50'>
                {adding ? 'Adding…' : 'Add domain'}
              </button>
            </div>
          </div>
          <div className='space-y-3'>
            {domains.map((d) => <DomainCard key={d.id} domain={d} onChange={updateDomain} onRemove={removeDomain} />)}
            {domains.length === 0 && <p className='text-sm text-slate-500'>No domains yet. Add the domain you will send emails from.</p>}
          </div>
        </section>

        <section className='bg-white rounded-xl border border-slate-200/70 shadow-sm overflow-hidden'>
          <div className='flex items-center justify-between px-5 py-4 border-b border-slate-200'>
            <h2 className='text-lg font-semibold text-slate-900'>Senders</h2>
            <button onClick={() => setModal({})} disabled={domains.length === 0} className='px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50' title={domains.length === 0 ? 'Add a domain first' : ''}>
              + New sender
            </button>
          </div>
          <table className='w-full text-sm'>
            <thead className='bg-slate-50 text-xs uppercase text-slate-500'>
              <tr>
                {['Domain', 'From Address', 'From Name', 'Status', 'Domain Status', 'Rate Limit', ''].map((h) => (
                  <th key={h} className='text-left font-medium px-5 py-2'>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {senders.map((s) => (
                <tr id={`email-sender-${s.id}`} key={s.id} className='hover:bg-slate-50'>
                  <td className='px-5 py-3 text-slate-700'>{s.domain}</td>
                  <td className='px-5 py-3 font-medium text-slate-900'>{s.from_address}</td>
                  <td className='px-5 py-3 text-slate-700'>{s.from_name}</td>
                  <td className='px-5 py-3'>
                    <button onClick={() => toggle(s)} className={`text-xs font-medium px-2 py-0.5 rounded ${s.active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {s.active ? 'Active' : 'Disabled'}
                    </button>
                  </td>
                  <td className='px-5 py-3'><StatusBadge status={s.domain_status} /></td>
                  <td className='px-5 py-3 text-slate-700'>
                    {s.rate_limit ? `${s.rate_limit}/min` : <span className='text-slate-400'>default ({defaultRateLimit}/min)</span>}
                    {s.throttle && (
                      <div
                        className='mt-1 text-xs text-amber-700'
                        title='The provider answered 429 (too many requests), so this sender sends slower for a while'
                      >
                        Slowed to {Math.round(s.throttle.factor * 100)}% until{' '}
                        {new Date(s.throttle.until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </td>
                  <td className='px-5 py-3 text-right whitespace-nowrap'>
                    <button onClick={() => setModal({ sender: s })} className='text-sm text-slate-600 hover:text-slate-900 mr-3'>Edit</button>
                    <button onClick={() => removeSender(s)} className='text-sm text-red-600 hover:text-red-800'>Delete</button>
                  </td>
                </tr>
              ))}
              {senders.length === 0 && (
                <tr><td colSpan='7' className='px-5 py-8 text-center text-slate-500'>No email senders yet.</td></tr>
              )}
            </tbody>
          </table>
          <p className='px-5 py-3 text-xs text-slate-500 border-t border-slate-100'>
            A sender can be used in a campaign only when it is active and its domain is verified.
          </p>
        </section>
      </div>

      {modal && (
        <Modal title={modal.sender ? 'Edit email sender' : 'New email sender'} onClose={() => setModal(null)}>
          <SenderForm initial={modal.sender} domains={domains} defaultRateLimit={defaultRateLimit} onSaved={onSaved} onCancel={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
};

export default EmailSendersManager;
